import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/api';
import { resetDismissalsCache } from '../utils/dismissals';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// LOT 79 · la dernière session vérifiée de cet onglet ouvre l'espace tout de suite ; /auth/me confirme en arrière-plan
const MOI = 'c360-moi';
const moiGarde = () => { try { const v = JSON.parse(sessionStorage.getItem(MOI) || 'null'); const seg = window.location.pathname.split('/')[1]; return v && seg && [v.tenant_slug, String(v.tenant_id || '').replace(/^tenant_/, '')].includes(seg) ? v : null; } catch { return null; } };
const garderMoi = (u) => { try { if (u) sessionStorage.setItem(MOI, JSON.stringify(u)); else sessionStorage.removeItem(MOI); } catch { /* stockage indisponible */ } };

export const AuthProvider = ({ children }) => {
  const [user, setUserBrut] = useState(moiGarde);
  const setUser = useCallback((u) => setUserBrut((avant) => { const v = typeof u === 'function' ? u(avant) : u; garderMoi(v); return v; }), []);
  const [suspendu, setSuspendu] = useState('');  // LOT 45 · accès suspendu par 360
  const [loading, setLoading] = useState(() => !moiGarde());

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const API = getApiUrl();
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      // V5.99.11 — Sync the server-side language preference if the user has one
      try {
        const lang = response.data?.language;
        if (lang && ['fr','en','ar'].includes(lang) && localStorage.getItem('lang') !== lang) {
          localStorage.setItem('lang', lang);
          // Force a soft reload of html lang/dir without reloading the bundle
          document.documentElement.setAttribute('lang', lang);
          document.documentElement.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
        }
      } catch (_e) { /* noop */ }
    } catch (error) {
      setUser(null);
      setSuspendu(error?.response?.status === 423 ? (error.response.data?.detail || 'Votre accès est suspendu. Contactez contact@360-resto.com.') : '');
      // LOT 33 · 401 : l'ancien stockage du navigateur est effacé (jamais de jeton dans localStorage)
      if (error?.response?.status === 401) { try { ['session_token', 'token', 'auth_token', 'user'].forEach((k) => { localStorage.removeItem(k); sessionStorage.removeItem(k); }); } catch (_e) { /* stockage indisponible */ } }
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    const API = getApiUrl();
    const source = user?.demo_origin || user?.tenant_id || window.location.pathname.split('/')[1];
    const slug = String(source || '').replace(/^tenant_/, '').replace(/^demo-/, '');
    const returnPath = /^[a-z0-9][a-z0-9-]*$/i.test(slug) && !['login','admin','auth'].includes(slug) ? `/${slug}/login` : '/login';
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      resetDismissalsCache();
      setUser(null);
      try { Object.keys(sessionStorage).filter((k) => k.startsWith('c360')).forEach((k) => sessionStorage.removeItem(k)); } catch { /* rien */ }
      window.location.assign(returnPath);
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Déconnexion impossible pour le moment. Réessayez sans quitter votre espace.');
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, suspendu }}>
      {children}
    </AuthContext.Provider>
  );
};